import { Component, Input, OnInit } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { EndpointService } from '../../../../../../@shared/endpoints.service';
import { MembershipPlanResponse, MembershipResponse, UserSearchResult } from '../../../../../../@shared/models/response.module';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { ToastService } from '../../../../../../@core/services/toast.service';

@Component({
  selector: 'app-add-member-modal',
  standalone: false,
  templateUrl: './add-member-modal.html',
  styleUrl: './add-member-modal.css'
})
export class AddMemberModal implements OnInit {
  @Input() plan: MembershipPlanResponse;
  @Input() companyId: string;

  memberType: 'existing' | 'manual' = 'existing';

  // Existing user search
  userSearchQuery: string = '';
  userSearchResults: UserSearchResult[] = [];
  selectedUser: UserSearchResult | null = null;
  isSearching: boolean = false;
  private searchSubject = new Subject<string>();

  // Manual member fields
  firstName: string = '';
  lastName: string = '';
  email: string = '';
  phone: string = '';

  // Payment fields
  paymentStatus: 'not_required' | 'pending' | 'paid' = 'paid';
  paymentMethod: 'stripe' | 'manual' | 'bank_transfer' | 'cash' = 'manual';
  paymentNote: string = '';

  // Expiry
  expiresAt: string = '';

  isSubmitting: boolean = false;
  errorMessage: string = '';
  submitted: boolean = false;

  constructor(
    public activeModal: NgbActiveModal,
    private endpointService: EndpointService,
    private toastService: ToastService
  ) {}

  // Validation helpers
  get isSelectedUserInvalid(): boolean {
    return this.submitted && this.memberType === 'existing' && this.selectedUser === null;
  }

  get isFirstNameInvalid(): boolean {
    return this.submitted && this.memberType === 'manual' && !this.firstName.trim();
  }

  get isLastNameInvalid(): boolean {
    return this.submitted && this.memberType === 'manual' && !this.lastName.trim();
  }

  ngOnInit() {
    // Set default expiry to end of year
    const endOfYear = new Date(new Date().getFullYear(), 11, 31);
    this.expiresAt = endOfYear.toISOString().split('T')[0];

    // Setup debounced user search
    this.searchSubject.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap(query => {
        if (query.length < 2) {
          return [];
        }
        this.isSearching = true;
        return this.endpointService.aqh_eve_67({ search: query });
      })
    ).subscribe({
      next: (response: any) => {
        this.isSearching = false;
        this.userSearchResults = response?.data || [];
      },
      error: () => {
        this.isSearching = false;
        this.userSearchResults = [];
      }
    });
  }

  onMemberTypeChange() {
    // Reset fields when switching types
    this.selectedUser = null;
    this.userSearchQuery = '';
    this.userSearchResults = [];
    this.firstName = '';
    this.lastName = '';
    this.email = '';
    this.phone = '';
    this.errorMessage = '';
    this.submitted = false;
  }

  onUserSearch() {
    this.searchSubject.next(this.userSearchQuery);
  }

  selectUser(user: UserSearchResult) {
    this.selectedUser = user;
    this.userSearchQuery = user.full_name || `${user.first_name} ${user.last_name}`;
    this.userSearchResults = [];
  }

  clearSelectedUser() {
    this.selectedUser = null;
    this.userSearchQuery = '';
  }

  isValid(): boolean {
    if (this.memberType === 'existing') {
      return this.selectedUser !== null;
    } else {
      return this.firstName.trim().length > 0 && this.lastName.trim().length > 0;
    }
  }

  submit() {
    this.submitted = true;

    if (!this.isValid()) {
      this.toastService.error('Please fill in all required fields');
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const payload: any = {
      plan_id: this.plan.plan_id,
      payment_status: this.paymentStatus,
      payment_method: this.paymentMethod,
      payment_note: this.paymentNote || null,
      expires_at: this.expiresAt || null
    };

    if (this.memberType === 'existing' && this.selectedUser) {
      payload.user_id = this.selectedUser.user_id;
    } else {
      payload.member_first_name = this.firstName.trim();
      payload.member_last_name = this.lastName.trim();
      payload.member_email = this.email.trim() || null;
      payload.member_phone = this.phone.trim() || null;
    }

    this.endpointService.aqh_mem_9(payload).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.toastService.success('Member added successfully');
        this.activeModal.close(response.data);
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage = error?.error?.reason || 'Failed to add member. Please try again.';
        this.toastService.error(this.errorMessage);
      }
    });
  }
}
